import React, { useState, useMemo } from "react";
import { MdExpandMore, MdExpandLess } from "react-icons/md";
import { FaHeartbeat } from "react-icons/fa";
import { MenuItem } from "../../redux/slices/menuSlice";
import clsx from "clsx";

interface NutritionalInfoProps {
  product: MenuItem;
}

const NutritionalInfo: React.FC<NutritionalInfoProps> = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 640 : true
  );

  const hasNutritionalInfo = useMemo(
    () => !!(product.Calories || product.Nutrients),
    [product.Calories, product.Nutrients]
  );

  if (!hasNutritionalInfo) return null;

  return (
    <section className="mb-6 sm:mb-8 border border-gray-200 rounded-lg overflow-hidden">
      {/* Toggle Button */}
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls="nutritional-content"
        className="w-full text-left px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <h2 className="text-lg sm:text-xl font-semibold flex items-center">
          <FaHeartbeat className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-500" />
          Nutritional Information
        </h2>
        {isExpanded ? (
          <MdExpandLess className="h-5 w-5 text-gray-500" />
        ) : (
          <MdExpandMore className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {/* Nutritional Data */}
      {isExpanded && (
        <div
          id="nutritional-content"
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 p-3 sm:p-4"
        >
          {product.Calories && (
            <NutrientBadge label="Calories" value={product.Calories} />
          )}
          {product.Nutrients && (
            <NutrientBadge label="Nutrients" value={product.Nutrients} />
          )}
        </div>
      )}
    </section>
  );
};

interface NutrientBadgeProps {
  label: string;
  value: string | number;
}

const NutrientBadge: React.FC<NutrientBadgeProps> = ({ label, value }) => {
  return (
    <span
      className={clsx(
        "px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm bg-gray-100",
        "whitespace-nowrap truncate"
      )}
    >
      {label}: {value}
    </span>
  );
};

export default NutritionalInfo;
